/**
 * Language as a path prefix, WebPaywalls style: `/ru/quiz/age`, `/ru/paywall`. The prefix becomes the
 * router `base`, so routes and `navigate()` stay unprefixed in code. Detection order mirrors WebPaywalls'
 * LanguageDetector: path → `?lang=` → `en`.
 */
export const SUPPORTED_LANGUAGES = ['en', 'ru', 'de', 'es', 'fr', 'it', 'pt'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export const isSupportedLanguage = (value: string | null | undefined): value is SupportedLanguage =>
  SUPPORTED_LANGUAGES.includes(String(value || '').toLowerCase() as SupportedLanguage);

export interface LangPath {
  lang: string;
  /** `'/ru'` when the path starts with a language, `''` otherwise. */
  basePath: string;
}

export const resolveLangPath = (pathname: string | undefined, queryLang?: string | null): LangPath => {
  const first = String(pathname || '').split('/').filter(Boolean)[0];
  if (first && isSupportedLanguage(first)) return { lang: first.toLowerCase(), basePath: `/${first.toLowerCase()}` };
  if (queryLang && isSupportedLanguage(queryLang)) return { lang: queryLang.toLowerCase(), basePath: '' };
  return { lang: queryLang || DEFAULT_LANGUAGE, basePath: '' };
};

/** `/ru/paywall` → `/paywall`; a pathname outside the base is returned as is. */
export const stripBasePath = (pathname: string, basePath: string): string => {
  if (!basePath) return pathname;
  if (pathname === basePath) return '/';
  return pathname.startsWith(`${basePath}/`) ? pathname.slice(basePath.length) : pathname;
};

export const withBasePath = (path: string, basePath: string): string => (basePath ? `${basePath}${path}` : path);
